import { gql, useMutation } from "@apollo/client";
import { datadogLogs } from "@datadog/browser-logs";
import {
  customerAddressCreateMutation,
  customerAddressUpdateMutation,
  customerDefaultAddressUpdateMutation,
} from "@repo/shopify/src/queries/customer-gql";
import type {
  ICustomerAddressCreateMutation,
  ICustomerAddressUpdateMutation,
  ICustomerDefaultAddressUpdateMutation,
} from "@repo/shopify/src/queries/customer-gql";
import {
  ProvinceNameEnMap,
  ProvinceNameFullEnMap,
  ProvinceNameKoMap,
  transformAddressFromDaum,
} from "@repo/utils/src/address";
import { checkPhone, transformPhoneWithDash } from "@repo/utils/src/number";
import type { MailingAddress } from "@shopify/hydrogen-react/storefront-api-types";
import React, { useEffect, useMemo, useState } from "react";
import DaumPostcodeEmbed from "react-daum-postcode";
import type { Address } from "react-daum-postcode/lib/loadPostcode";
import { type SubmitErrorHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Button from "@components/atoms/Button";
import Icon from "@components/atoms/Icon";
import Checkbox from "@components/atoms/Input/Checkbox";
import Input from "@components/atoms/Input/Input";

import useLayout from "@hooks/reducerHooks/useLayout";
import useUser from "@hooks/reducerHooks/useUser";

import { IconName } from "@const/enum";

import { NOTIFICATIONS } from "@utils/notifications";

interface IProps {
  onClickSubmit?: () => void;
  onClickCancel?: () => void;
  address?: MailingAddress & { isDefaultAddress: boolean };
}

/** 배송지 수정 Form */
function AddressForm({ onClickSubmit, onClickCancel, address }: IProps) {
  const { openModal, modal, closeModal } = useLayout();
  const { fetchUser, accessToken } = useUser();
  const { t } = useTranslation();

  const [helpMessage, setHelpMessage] = useState(""); // 주소 form validation 메세지
  const [firstErrorKey, setFirstErrorKey] = useState(""); // 주소 form 첫번째 error key
  const [checkDefault, setCheckDefault] = useState(false); // 주소 form 기본배송지 체크 여부
  const [saveLoading, setSaveLoading] = useState(false); // 주소 form 저장 중 여부

  useEffect(() => {
    if (!modal.title) closeModal();
  }, [modal.title]);

  const defaultValues = useMemo(
    () =>
      address
        ? {
            address1: address.address1,
            address2: address.address2,
            firstName: address.firstName,
            lastName: address.lastName,
            phone: address.phone,
            province: address.province ? ProvinceNameKoMap[address.province] : "",
            zip: address.zip,
          }
        : undefined,
    [address],
  );

  /**
   * 주소 form hook
   * defaultValues 값이 있을 경우 form에 넣어주기
   * */
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    reset,
    clearErrors,
    setFocus,
    formState: { errors },
  } = useForm<MailingAddress>({ defaultValues, shouldFocusError: false });

  /**
   * DaumPostcodeEmbed 주소 검색 값을 form 데이터로 setValue 해주는 function
   * country: shopify 쿼리에 사용. TODO: 'South Korea'로 하드코딩(변경 여부 확인 필요)
   * company: '' 빈값 하드코딩
   * province: 다음 주소 검색 결과 sido(한글)를 shopify에서 사용하는 영문으로 ProvinceNameMap 매핑. shopify 쿼리에 사용
   * city: 다음 주소 검색 결과 sido(한글) 입력 필드 UI에서 노출, 주소 저장할 때는 '-' 로 변경. shopify에서는 사용 안 됨
   * */
  const handleComplete = (data: Address) => {
    setValue("address1", transformAddressFromDaum(data));
    setValue("province", ProvinceNameKoMap[ProvinceNameEnMap[data.sido]]);
    setValue("zip", data.zonecode);
    setFocus("address2");
    closeModal();
  };

  /** 기본배송지 변경 */
  const [mutateDefaultAddress] = useMutation<ICustomerDefaultAddressUpdateMutation>(
    gql(customerDefaultAddressUpdateMutation.query),
    {
      onError: (error) => {
        datadogLogs.logger.error("[SHOPIFY][CustomerDefaultAddressUpdate] Failed to mutation", error);
        throw new Error(`[SHOPIFY][CustomerDefaultAddressUpdate] Failed to mutation : ${JSON.stringify(error)}`);
      },
    },
  );

  const [createCustomerAddress] = useMutation<ICustomerAddressCreateMutation>(
    gql(customerAddressCreateMutation.query),
    {
      onCompleted: (data) => {
        // 기본배송지 체크 확인
        if (!checkDefault) {
          // onClickSubmit?.();
        } else {
          // 기본배송지 체크되어 있을 때 mutate 호출
          void mutateDefaultAddress({
            variables: {
              addressId: data.customerAddressCreate.customerAddress?.id || "",
              customerAccessToken: accessToken,
            },
          });
          // onClickSubmit?.();
        }
      },
      onError: (error) => {
        datadogLogs.logger.error("[SHOPIFY][CustomerAddressCreate] Failed to mutation", error);
        throw new Error(`[SHOPIFY][CustomerAddressCreate] Failed to mutation : ${JSON.stringify(error)}`);
      },
    },
  );

  /** 배송지 수정 */
  const [updateCustomerAddress, { loading: updateLoading }] = useMutation<ICustomerAddressUpdateMutation>(
    gql(customerAddressUpdateMutation.query),
    {
      onCompleted: (data) => {
        // 기본배송지 체크 확인
        if (!checkDefault) {
          // onClickSubmit?.();
        } else {
          // 기본배송지 체크되어 있을 때 mutate 호출
          void mutateDefaultAddress({
            variables: {
              addressId: data.customerAddressUpdate.customerAddress?.id || "",
              customerAccessToken: accessToken,
            },
          });
          // onClickSubmit?.();
        }
      },
      onError: (error) => {
        datadogLogs.logger.error("[SHOPIFY][customerAddressUpdate] Failed to mutation", error);
        throw new Error(`[SHOPIFY][customerAddressUpdate] Failed to mutation : ${JSON.stringify(error)}`);
      },
    },
  );

  /** 주소 검색 모달 */
  const openAddressModal = () => {
    openModal({
      title: t("account.shipping.address.find"),
      closeButton: true,
      description: "",
      children: (
        <DaumPostcodeEmbed
          onComplete={handleComplete}
          style={{ overflow: "hidden", borderRadius: "10px", height: "395px" }}
        />
      ),
      hasButton: true,
    });
  };

  /** 저장/추가하기 버튼 클릭 */
  const onSubmit = async (data: MailingAddress) => {
    const inputAddress = {
      lastName: data.lastName,
      firstName: data.firstName,
      zip: data.zip,
      address1: data.address1,
      address2: data.address2,
      country: "South Korea",
      province: data.province ? ProvinceNameFullEnMap[data.province] : "",
      phone: transformPhoneWithDash(data.phone || ""),
    };

    setSaveLoading(true);
    // 수정
    if (address?.id) {
      await updateCustomerAddress({
        variables: { customerAccessToken: accessToken, id: address.id, address: inputAddress },
      });
    } else {
      // 생성
      await createCustomerAddress({ variables: { customerAccessToken: accessToken, address: inputAddress } });
    }

    await fetchUser({ variables: { customerAccessToken: accessToken } });

    onClickSubmit?.();
    setSaveLoading(false);

    // 토스트, form, error, 체크박스, firstError, helpMessage 초기화
    reset();
    clearErrors();
    setCheckDefault(false);
    setFirstErrorKey("");
    setHelpMessage("");
  };

  const onError: SubmitErrorHandler<MailingAddress> = (error) => {
    const sortedKeys = Object.keys(getValues());
    sortedKeys.some((key) => {
      if (error[key as keyof typeof error]) {
        setFirstErrorKey(key);
        return true;
      }
      return false;
    });
  };

  const displayError = (key: string) => {
    return { hasError: firstErrorKey === key };
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <div className="flex flex-col gap-2.5">
          <div className="flex gap-2.5">
            <Input
              {...(errors.lastName && displayError("lastName"))}
              {...register("lastName", {
                validate(value) {
                  if (!value?.trim()) return NOTIFICATIONS.WARN_SHIPPING_ADDRESS.message;
                },
              })}
              maxLength={6}
              placeholder={t("account.shipping.address.last_name")}
              required
              variant="white"
            />
            <Input
              {...(errors.firstName && displayError("firstName"))}
              {...register("firstName", {
                validate(value) {
                  if (!value?.trim()) return NOTIFICATIONS.WARN_SHIPPING_ADDRESS.message;
                },
              })}
              maxLength={6}
              placeholder={t("account.shipping.address.first_name")}
              required
              variant="white"
            />
          </div>
          <div>
            <Input
              {...(errors.zip && displayError("zip"))}
              {...register("zip", {
                validate(value) {
                  if (!value?.trim()) return NOTIFICATIONS.WARN_SHIPPING_ADDRESS.message;
                },
              })}
              className="cursor-pointer"
              name="zip"
              onClick={openAddressModal}
              placeholder={t("account.shipping.address.zip")}
              readOnly
              required
              rightIconNode={
                <Button onClick={openAddressModal} variant="icon">
                  <Icon name={IconName.Search_100} />
                </Button>
              }
              variant="white"
            />
          </div>
          <div>
            <Input
              {...(errors.province && displayError("province"))}
              {...register("province", {
                validate(value) {
                  if (!value?.trim()) return NOTIFICATIONS.WARN_SHIPPING_ADDRESS.message;
                },
              })}
              placeholder={t("account.shipping.address.city")}
              readOnly
              required
              variant="white"
            />
          </div>
          <div>
            <Input
              {...(errors.address1 && displayError("address1"))}
              {...register("address1", {
                validate(value) {
                  if (!value?.trim()) return NOTIFICATIONS.WARN_SHIPPING_ADDRESS.message;
                },
              })}
              placeholder={t("account.shipping.address.address1")}
              readOnly
              required
              variant="white"
            />
          </div>
          <div>
            <Input
              {...(errors.address2 && displayError("address2"))}
              {...register("address2", {
                validate(value) {
                  if (!value?.trim()) return NOTIFICATIONS.WARN_SHIPPING_ADDRESS.message;
                },
              })}
              placeholder={t("account.shipping.address.address2")}
              required
              variant="white"
            />
          </div>
          <div>
            <Input
              {...(errors.phone && displayError("phone"))}
              {...register("phone", {
                validate(value) {
                  if (!value?.trim()) return NOTIFICATIONS.WARN_SHIPPING_ADDRESS.message;
                  if (!checkPhone(value)) return NOTIFICATIONS.WARN_PHONE_VALID.message;
                },
              })}
              maxLength={11}
              placeholder={t("account.shipping.address.phone")}
              required
              type="tel"
              variant="white"
            />
          </div>
          {Boolean(helpMessage) && <span className="font-regular text-red-400">{helpMessage}</span>}
        </div>
        {!address?.isDefaultAddress && (
          <div className="mt-5 inline-block">
            <Checkbox
              checked={checkDefault}
              label={t("account.shipping.address.set_default")}
              onChange={(e) => {
                setCheckDefault(e.target.checked);
              }}
            />
          </div>
        )}
      </form>
      <div className="mt-5 flex items-center justify-center gap-2.5">
        {Boolean(onClickCancel) && (
          <Button onClick={onClickCancel} styleType="secondary">
            <span>{t("login.cancel")}</span>
          </Button>
        )}
        <Button isLoading={saveLoading} onClick={handleSubmit(onSubmit, onError)} type="submit">
          <span>{defaultValues ? t("account.shipping.address.save") : t("account.shipping.address.add")}</span>
        </Button>
      </div>
    </>
  );
}

export default AddressForm;
